import { isAxiosError } from 'axios'
import { PaytrieAPI } from '@paytrie/client'

import { logger } from '@/utils/logger'

import { configurePaytrieClient } from './apiClient'

type ApiResult<T> = { ok: true; data: T } | { ok: false; message: string }

const getErrorMessage = (res: { data: unknown }, fallback: string): string => {
  if (
    res.data &&
    typeof res.data === 'object' &&
    'errors' in res.data &&
    Array.isArray((res.data as { errors?: Array<{ message?: string }> }).errors)
  ) {
    const message = (res.data as { errors?: Array<{ message?: string }> })
      .errors?.[0]?.message
    if (message) return message
  }
  return fallback
}

export type SendSMSResult =
  | { kind: 'success' }
  | { kind: 'error'; message: string }

export async function sendSMS(phoneNumber: string): Promise<SendSMSResult> {
  if (!configurePaytrieClient()) {
    return {
      kind: 'error',
      message: 'Authentication required. Please sign in again.',
    }
  }

  try {
    const res = await PaytrieAPI.sendPhoneVerification({
      phone: phoneNumber,
    })

    if (res.status === 202) {
      return { kind: 'success' }
    }
    return {
      kind: 'error',
      message: getErrorMessage(res, 'Unable to send SMS verification code.'),
    }
  } catch {
    return { kind: 'error', message: 'Unable to send SMS verification code.' }
  }
}

export type VerifySMSResult =
  | { kind: 'success' }
  | { kind: 'phone_already_claimed' }
  | { kind: 'error'; message: string }

export async function verifySMS(phoneCode: string): Promise<VerifySMSResult> {
  if (!configurePaytrieClient()) {
    return {
      kind: 'error',
      message: 'Authentication required. Please sign in again.',
    }
  }

  try {
    const res = await PaytrieAPI.verifyPhone({ code: phoneCode })

    if (res.status === 409) {
      return { kind: 'phone_already_claimed' }
    }
    if (res.status !== 204) {
      return {
        kind: 'error',
        message: getErrorMessage(res, 'Unable to verify phone number.'),
      }
    }
    return { kind: 'success' }
  } catch {
    return { kind: 'error', message: 'Unable to verify phone number.' }
  }
}

export async function updateCurrentFlow(
  currentFlow: PaytrieAPI.UpdateCurrentUserBodyCurrentFlow
): Promise<void> {
  if (!configurePaytrieClient()) return

  try {
    await PaytrieAPI.updateCurrentUser({ currentFlow })
  } catch (error) {
    logger.warn('Failed to update currentFlow tracking', {
      currentFlow,
      error: isAxiosError(error) ? error.response?.data : error,
    })
  }
}

export async function patchOnboardingSource(
  onboardingSource: PaytrieAPI.UpdateCurrentUserBodyOnboardingSource
): Promise<ApiResult<void>> {
  if (!configurePaytrieClient()) {
    return {
      ok: false,
      message: 'Authentication required. Please sign in again.',
    }
  }

  try {
    const res = await PaytrieAPI.updateCurrentUser({ onboardingSource })

    if (res.status !== 200) {
      return {
        ok: false,
        message: getErrorMessage(
          res,
          'Unable to record onboarding channel. Please try again.'
        ),
      }
    }

    return { ok: true, data: undefined }
  } catch {
    return {
      ok: false,
      message: 'Unable to record onboarding channel. Please try again.',
    }
  }
}

export async function patchPersonalInfo(body: {
  firstName: string
  lastName: string
  dob: string
  addressLine1: string
  addressLine2: string
  city: string
  province: string
  postalCode: string
}): Promise<ApiResult<void>> {
  if (!configurePaytrieClient()) {
    return {
      ok: false,
      message: 'Authentication required. Please sign in again.',
    }
  }

  try {
    const res = await PaytrieAPI.updateCurrentUser({
      firstName: body.firstName.trim(),
      lastName: body.lastName.trim(),
      dob: body.dob,
      addressLine1: body.addressLine1.trim(),
      addressLine2: body.addressLine2.trim() || null,
      city: body.city.trim(),
      province:
        body.province.toLowerCase() as PaytrieAPI.UpdateCurrentUserBodyProvince,
      postalCode: body.postalCode.replace(/\s/g, '').toLowerCase(),
      currentFlow: PaytrieAPI.UpdateCurrentUserBodyCurrentFlow.PERSONAL_INFO,
    })

    if (res.status !== 200) {
      return {
        ok: false,
        message: getErrorMessage(
          res,
          'Unable to save personal information. Please try again.'
        ),
      }
    }

    return { ok: true, data: undefined }
  } catch {
    return {
      ok: false,
      message: 'Unable to save personal information. Please try again.',
    }
  }
}

export function buildOccupationValue(
  employmentStatus: string,
  occupation: string,
  occupationDetails: string
): string {
  const isEmployedLike =
    employmentStatus === 'Employed' || employmentStatus === 'Self-Employed'

  const innerOccupation = isEmployedLike
    ? occupation === 'Other'
      ? `other:${occupationDetails.trim()}`
      : occupation.trim().toLowerCase()
    : employmentStatus.trim().toLowerCase()

  const completeOccupation =
    employmentStatus === 'Self-Employed'
      ? `self employed:${innerOccupation}`
      : innerOccupation

  return completeOccupation.trim().toLowerCase()
}

export async function patchOccupationInfo(body: {
  employmentStatus: string
  occupation: string
  occupationDetails: string
  purpose: string
}): Promise<ApiResult<void>> {
  if (!configurePaytrieClient()) {
    return {
      ok: false,
      message: 'Authentication required. Please sign in again.',
    }
  }

  const occupationValue = buildOccupationValue(
    body.employmentStatus,
    body.occupation,
    body.occupationDetails
  )

  try {
    const res = await PaytrieAPI.updateCurrentUser({
      occupation: occupationValue,
      purpose: body.purpose.trim(),
      pep: false,
      tpd: false,
      currentFlow: PaytrieAPI.UpdateCurrentUserBodyCurrentFlow.OCCUPATION,
    })

    if (res.status !== 200) {
      if (res.status === 409) {
        return {
          ok: false,
          message:
            'An account with this information already exists. Please contact support@paytrie.com.',
        }
      }
      return {
        ok: false,
        message: getErrorMessage(
          res,
          'Unable to save employment information. Please try again.'
        ),
      }
    }

    return { ok: true, data: undefined }
  } catch {
    return {
      ok: false,
      message: 'Unable to save employment information. Please try again.',
    }
  }
}

export async function patchOlderUserInfo(
  olderUserInfo: NonNullable<
    Parameters<typeof PaytrieAPI.updateCurrentUser>[0]['olderUserInfo']
  >,
  currentFlow:
    | typeof PaytrieAPI.UpdateCurrentUserBodyCurrentFlow.ADDITIONAL_INFO
    | typeof PaytrieAPI.UpdateCurrentUserBodyCurrentFlow.ADDITIONAL_INFO_PAGE2
): Promise<ApiResult<void>> {
  if (!configurePaytrieClient()) {
    return {
      ok: false,
      message: 'Authentication required. Please sign in again.',
    }
  }

  try {
    const res = await PaytrieAPI.updateCurrentUser({
      olderUserInfo,
      currentFlow,
    })

    if (res.status !== 200) {
      return {
        ok: false,
        message: getErrorMessage(
          res,
          'Unable to save additional information. Please try again.'
        ),
      }
    }

    return { ok: true, data: undefined }
  } catch {
    return {
      ok: false,
      message: 'Unable to save additional information. Please try again.',
    }
  }
}
